// v2 tall grass — an arching tuft: two-segment blades that rise from a tight
// crown and bend over at the top, the three tallest tipped with pale elongated
// seed heads, so the tuft reads as ripe meadow grass nodding under its own
// weight. Variants: fresh green, sun-dried gold and olive steppe, each with its
// own turn. Ported 1:1 from age-of-mcp's v2/tall-grass.ts, using the library's
// shared `groundcover` (bladeArc, dirAt, tiltRot, h01, lerp) helpers.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const TAU = Math.PI * 2;
const TONES: [string, string, string][] = [
  ["#6fae4e", "#82bf5a", "#d8c87a"],   // fresh green, straw heads
  ["#b3a14e", "#c4b258", "#cdb45e"],   // sun-dried gold
  ["#8a9a4a", "#9aaa54", "#c9bc6e"],   // olive steppe
];

function tallGrassParts(v: number): Part[] {
  const seed = v * 29 + 13;
  const yaw = v * 2.4 + 0.9;
  const [colA, colB, head] = TONES[v % TONES.length]!;
  const parts: Part[] = [];
  const N = 6;
  for (let i = 0; i < N; i++) {
    const ang = yaw + (i / N) * TAU + groundcover.h01(seed, i) * 0.7;
    // even blades are the tall, seed-bearing ones
    const len = i % 2 === 0
      ? groundcover.lerp(0.38, 0.44, groundcover.h01(seed, i + 6))
      : groundcover.lerp(0.28, 0.34, groundcover.h01(seed, i + 6));
    const arc = groundcover.bladeArc({
      x: Math.cos(ang) * 0.045, z: Math.sin(ang) * 0.045, ang,
      lean: groundcover.lerp(0.08, 0.2, groundcover.h01(seed, i + 12)),
      arch: groundcover.lerp(0.35, 0.6, groundcover.h01(seed, i + 18)),
      len, w: 0.021, t: 0.006, color: i % 2 ? colB : colA,
    });
    parts.push(...arc.parts);
    if (i % 2 === 0) {
      // a pale elongated seed head nodding off the blade tip
      const d = groundcover.dirAt(ang, arc.tipTilt);
      parts.push({
        geometry: new THREE.IcosahedronGeometry(0.014, 0),
        color: head,
        position: [arc.tip[0] + d[0] * 0.022, arc.tip[1] + d[1] * 0.022,
          arc.tip[2] + d[2] * 0.022],
        rotation: groundcover.tiltRot(ang, arc.tipTilt),
        scale: [1, 2.1, 0.9],
      });
    }
  }
  return parts;
}

export const tallGrassV2 = defineModel({
  id: "tall-grass-v2",
  batchId: "fable",
  name: "Tall grass (v2)",
  tags: ["scenery","tall","grass","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tall grass.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.26,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => tallGrassParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => tallGrassParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => tallGrassParts(2) },
  ],
  animations: [],
});
